import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  getDeliveryNotes, downloadCertificate, downloadPackingList, downloadCertificatesBulk, downloadPackingListsBulk, deleteDeliveryNote, updateDeliveryNoteRefs, getOrders, getPackagingOptions, fmt,
} from "@/lib/api";
import { Truck, FileDown, Eye, PackageCheck, ClipboardList, Trash2, Printer, FileText } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useT } from "@/i18n/I18nContext";
import { formatDate } from "@/lib/dates";
import { useProductTypes } from "@/context/ProductTypesContext";
import { usePagination } from "@/hooks/usePagination";
import { TablePagination } from "@/components/TablePagination";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function DeliveryNotes() {
  const { can } = useAuth();
  const { t, td, lang } = useT();
  const canDelete = can("admin", "manager");
  const [notes, setNotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [packagings, setPackagings] = useState([]);
  const [day, setDay] = useState(todayStr());
  const [view, setView] = useState(null);
  const [delDn, setDelDn] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [printing, setPrinting] = useState(false);
  const [filterClient, setFilterClient] = useState("all");

  // Unique client options derived from the issued delivery notes.
  const clientOptions = useMemo(() => {
    const m = new Map();
    (notes || []).forEach((n) => {
      if (n.client_id && !m.has(n.client_id)) m.set(n.client_id, n.client_snapshot?.name || n.client_id);
    });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [notes]);

  const filteredNotes = useMemo(
    () => (filterClient === "all" ? notes : notes.filter((n) => n.client_id === filterClient)),
    [notes, filterClient],
  );
  const pager = usePagination(filteredNotes, 50);

  const load = async () => {
    const [n, o, pk] = await Promise.all([getDeliveryNotes(), getOrders(), getPackagingOptions()]);
    setNotes(n); setOrders(o); setPackagings(pk || []);
  };
  useEffect(() => { (async () => { await load(); })(); }, []);

  const netMap = useMemo(() => {
    const m = {};
    (packagings || []).forEach((p) => { m[p.label] = Number(p.net_kg) || (p.label === "kg" ? 1 : 0); });
    if (!m.kg) m.kg = 1;
    return m;
  }, [packagings]);

  // Open orders scheduled for the selected day (undated open orders fall under today).
  const dayOrders = useMemo(() => {
    return (orders || []).filter((o) => {
      if (o.status !== "placed" && o.status !== "confirmed") return false;
      const rd = (o.requested_date || "").slice(0, 10);
      return rd ? rd === day : day === todayStr();
    });
  }, [orders, day]);

  // Aggregate goods to load: product + packaging -> total qty + total kg
  const loadlist = useMemo(() => {
    const agg = {};
    dayOrders.forEach((o) => (o.items || []).forEach((it) => {
      const key = `${it.product_type}|${it.unit}`;
      const net = netMap[it.unit] != null ? netMap[it.unit] : (it.unit === "kg" ? 1 : 0);
      agg[key] = agg[key] || { product_type: it.product_type, unit: it.unit, qty: 0, kg: 0 };
      agg[key].qty += Number(it.quantity) || 0;
      agg[key].kg += (Number(it.quantity) || 0) * net;
    }));
    return Object.values(agg).sort((a, b) => a.product_type.localeCompare(b.product_type) || a.unit.localeCompare(b.unit));
  }, [dayOrders, netMap]);

  const totalKg = useMemo(() => loadlist.reduce((s, r) => s + (Number(r.kg) || 0), 0), [loadlist]);

  // Packaging-first summary: for each packaging, the products + subtotal packs & kg.
  const byPackaging = useMemo(() => {
    const m = {};
    loadlist.forEach((r) => {
      m[r.unit] = m[r.unit] || { unit: r.unit, qty: 0, kg: 0, items: [] };
      m[r.unit].qty += r.qty;
      m[r.unit].kg += r.kg;
      m[r.unit].items.push({ product_type: r.product_type, qty: r.qty });
    });
    return Object.values(m)
      .map((g) => ({ ...g, items: [...g.items].sort((a, b) => b.qty - a.qty) }))
      .sort((a, b) => {
        if (a.unit === "kg") return 1;        // bulk kg last
        if (b.unit === "kg") return -1;
        return a.unit.localeCompare(b.unit);
      });
  }, [loadlist]);

  const download = async (dn) => {
    try { await downloadCertificate(dn.id, dn.dn_code, lang); }
    catch (e) { toast.error(t("deliv.failedDownload")); }
  };
  const downloadPacking = async (dn) => {
    try { await downloadPackingList(dn.id, dn.dn_code, lang); }
    catch (e) { toast.error(t("deliv.failedDownload")); }
  };

  const allSelected = filteredNotes.length > 0 && filteredNotes.every((n) => selected.has(n.id));
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filteredNotes.map((n) => n.id)));

  const printBulk = async (kind) => {
    const ids = notes.filter((n) => selected.has(n.id)).map((n) => n.id);
    if (ids.length === 0) { toast.error(t("deliv.selectToPrint")); return; }
    setPrinting(true);
    try {
      if (kind === "packing") await downloadPackingListsBulk(ids, lang);
      else await downloadCertificatesBulk(ids, lang);
      toast.success(t("deliv.printedToast", { n: ids.length }));
    } catch (e) { toast.error(t("deliv.failedDownload")); }
    finally { setPrinting(false); }
  };

  const removeDn = async () => {
    if (!delDn) return;
    try {
      await deleteDeliveryNote(delDn.id);
      toast.success(t("deliv.noteDeleted", { code: delDn.dn_code }));
      setDelDn(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("deliv.failedGenerate")); }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Daily delivery prep — what to load in the car */}
        <Card data-testid="delivery-prep-card">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <ClipboardList className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("deliv.prepTitle")}
              </CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("deliv.prepSubtitle")}
              </p>
            </div>
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t("deliv.deliveryDay")}</Label>
                <Input type="date" value={day} onChange={(e) => setDay(e.target.value || todayStr())} className="h-9 w-[170px]" data-testid="delivery-prep-date" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {dayOrders.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-center">
                <PackageCheck className="h-9 w-9 text-muted-foreground/40" />
                <p className="mt-2 text-sm text-muted-foreground">{t("deliv.noOpenOrders", { day })}</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Load by packaging — grab all same-format items together */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{t("deliv.loadByPackaging")}</span>
                    <Badge className="bg-[hsl(var(--brand))] text-white" data-testid="delivery-prep-total-kg">{t("deliv.totalKg", { kg: fmt(totalKg, 2) })}</Badge>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {byPackaging.map((pk) => (
                      <div key={pk.unit} className="rounded-xl border border-border bg-card p-3.5" data-testid={`prep-pkg-${pk.unit}`}>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="font-mono text-[10px]">{pk.unit}</Badge>
                          <div className="text-right">
                            <div className="font-mono text-lg font-semibold leading-none tabular-nums" data-testid={`prep-pkg-qty-${pk.unit}`}>
                              {fmt(pk.qty, 2)}
                              <span className="ml-1 text-xs font-normal text-muted-foreground">{pk.unit === "kg" ? "kg" : t("deliv.packs")}</span>
                            </div>
                            <div className="mt-0.5 text-[11px] text-muted-foreground">≈ {fmt(pk.kg, 2)} kg</div>
                          </div>
                        </div>
                        <div className="mt-2.5 flex flex-wrap gap-1.5">
                          {pk.items.map((it) => (
                              <span key={it.product_type} className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                                {td(it.product_type)}
                                <span className="font-mono font-semibold tabular-nums">×{fmt(it.qty, 2)}</span>
                              </span>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Detailed product × packaging table + orders in this run */}
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                  <div className="lg:col-span-7">
                    <div className="mb-2">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">{t("deliv.detailedBreakdown")}</span>
                    </div>
                    <div className="overflow-x-auto thin-scroll rounded-lg border border-border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("common.product")}</TableHead>
                            <TableHead>{t("common.packaging")}</TableHead>
                            <TableHead className="text-right">{t("deliv.packsQty")}</TableHead>
                            <TableHead className="text-right">{t("deliv.approxKg")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {loadlist.map((r) => (
                            <TableRow key={`${r.product_type}|${r.unit}`} data-testid={`prep-row-${r.product_type}-${r.unit}`}>
                              <TableCell className="font-medium">{td(r.product_type)}</TableCell>
                              <TableCell><Badge variant="outline" className="font-mono text-[10px]">{r.unit}</Badge></TableCell>
                              <TableCell className="text-right font-mono tabular-nums">{fmt(r.qty, 2)}</TableCell>
                              <TableCell className="text-right font-mono tabular-nums text-muted-foreground">{fmt(r.kg, 2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <div className="lg:col-span-5">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{t("deliv.ordersForRun", { n: dayOrders.length })}</span>
                    <div className="mt-2 space-y-1.5">
                      {dayOrders.map((o) => (
                        <div key={o.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`prep-order-${o.id}`}>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium">{o.client_name}</span>
                            <span className="font-mono text-[11px] text-muted-foreground">{o.order_code} · {t("deliv.itemsCount", { n: o.items?.length || 0 })}</span>
                          </span>
                          <Badge className={o.status === "confirmed" ? "badge-ok" : "badge-warn"}>{t(`status.${o.status}`)}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            )}
          </CardContent>
        </Card>

        {/* Issued delivery notes */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Truck className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("deliv.title")} ({filteredNotes.length})
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={filterClient} onValueChange={setFilterClient}>
                  <SelectTrigger className="h-9 w-[200px]" data-testid="dn-client-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.allClients")}</SelectItem>
                    {clientOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={selected.size === 0 || printing}
                    data-testid="dn-print-selected-button"
                  >
                    <Printer className="mr-1.5 h-4 w-4" />
                    {selected.size > 0 ? t("deliv.printSelected", { n: selected.size }) : t("deliv.printSelectedEmpty")}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => printBulk("coc")} data-testid="dn-print-coc">
                    <FileDown className="mr-2 h-4 w-4" /> {t("deliv.printCerts")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => printBulk("packing")} data-testid="dn-print-packing">
                    <FileText className="mr-2 h-4 w-4" /> {t("deliv.printPackingLists")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {notes.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center">
                <Truck className="h-10 w-10 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">{t("deliv.empty")}</p>
              </div>
            ) : filteredNotes.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center" data-testid="dn-no-match">
                <Truck className="h-10 w-10 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">{t("common.noMatchClient")}</p>
              </div>
            ) : (
              <div className="overflow-x-auto thin-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleAll}
                          aria-label={t("deliv.selectAll")}
                          data-testid="dn-select-all"
                        />
                      </TableHead>
                      <TableHead>{t("deliv.deliveryNote")}</TableHead>
                      <TableHead>{t("orders.order")}</TableHead>
                      <TableHead>{t("common.client")}</TableHead>
                      <TableHead>{t("common.date")}</TableHead>
                      <TableHead className="text-right">{t("common.total")}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pager.pageItems.map((dn) => (
                      <TableRow key={dn.id} data-testid={`dn-row-${dn.id}`} data-state={selected.has(dn.id) ? "selected" : undefined}>
                        <TableCell>
                          <Checkbox
                            checked={selected.has(dn.id)}
                            onCheckedChange={() => toggleOne(dn.id)}
                            aria-label={t("deliv.selectOne", { code: dn.dn_code })}
                            data-testid={`dn-select-${dn.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{dn.dn_code}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{dn.order_code}</TableCell>
                        <TableCell className="font-medium">{dn.client_snapshot?.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDate(dn.date)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{fmt(dn.total, 2)} {dn.currency}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="icon" title={t("common.view")} onClick={() => setView(dn)} data-testid={`dn-view-${dn.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title={t("deliv.downloadCertShort")} onClick={() => download(dn)} data-testid={`dn-download-${dn.id}`}>
                            <FileDown className="h-4 w-4 text-[hsl(var(--brand))]" />
                          </Button>
                          {canDelete && (
                            <Button variant="ghost" size="icon" title={t("deliv.deleteBtn")} onClick={() => setDelDn(dn)} data-testid={`dn-delete-${dn.id}`}>
                              <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  page={pager.page} pageCount={pager.pageCount} pageSize={pager.pageSize}
                  total={pager.total} start={pager.start} end={pager.end}
                  onPageChange={pager.setPage} onPageSizeChange={pager.setPageSize}
                  testid="dn-pagination"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ViewDnDialog key={view?.id || "none"} dn={view} onClose={() => setView(null)} onDownload={download} onDownloadPacking={downloadPacking} onSaved={load} />

      <AlertDialog open={!!delDn} onOpenChange={(o) => !o && setDelDn(null)}>
        <AlertDialogContent data-testid="dn-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deliv.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {delDn ? t("deliv.deleteDesc", { code: delDn.dn_code, client: delDn.client_snapshot?.name || "" }) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dn-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={removeDn} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="dn-delete-confirm">{t("deliv.deleteAndRestore")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function ViewDnDialog({ dn, onClose, onDownload, onDownloadPacking, onSaved }) {
  const { t, td } = useT();
  const { all: ptAll } = useProductTypes();
  const [orderNo, setOrderNo] = useState(dn?.custom_order_no || "");
  const [invoiceNo, setInvoiceNo] = useState(dn?.invoice_no || "");
  const [invoiceType, setInvoiceType] = useState(dn?.invoice_type || "invoice");
  const [saving, setSaving] = useState(false);
  if (!dn) return null;
  const cs = dn.client_snapshot || {};
  const dirty = orderNo !== (dn.custom_order_no || "") || invoiceNo !== (dn.invoice_no || "") || invoiceType !== (dn.invoice_type || "invoice");
  const ptMeta = Object.fromEntries((ptAll || []).map((p) => [p.name, { code: p.code || "", storage_temp: p.storage_temp || "" }]));
  const daysLeft = (expiry) => {
    if (!expiry) return null;
    const e = new Date(`${String(expiry).slice(0, 10)}T00:00:00`);
    const r = new Date(`${String(dn.date).slice(0, 10)}T00:00:00`);
    if (isNaN(e) || isNaN(r)) return null;
    return Math.round((e - r) / 86400000);
  };

  const saveRefs = async () => {
    setSaving(true);
    try {
      await updateDeliveryNoteRefs(dn.id, { custom_order_no: orderNo, invoice_no: invoiceNo, invoice_type: invoiceType });
      toast.success(t("deliv.refsSaved"));
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!dn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">{dn.dn_code}</DialogTitle>
          <DialogDescription>{cs.name} · {formatDate(dn.date)}</DialogDescription>
        </DialogHeader>
        <div className="text-xs text-muted-foreground">
          {dn.delivery_address || cs.address}{cs.tax_id ? ` · ${cs.tax_id}` : ""}
        </div>

        <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("deliv.customOrderNo")}</Label>
            <Input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} placeholder={t("deliv.notSet")} data-testid="dn-order-no-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("deliv.invoiceDocType")}</Label>
            <Select value={invoiceType} onValueChange={setInvoiceType}>
              <SelectTrigger data-testid="dn-invoice-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="invoice" data-testid="dn-invoice-type-invoice">{t("deliv.invoiceTypeInvoice")}</SelectItem>
                <SelectItem value="dispatch" data-testid="dn-invoice-type-dispatch">{t("deliv.invoiceTypeDispatch")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{invoiceType === "dispatch" ? t("deliv.dispatchNo") : t("deliv.invoiceNo")}</Label>
            <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder={t("deliv.notSet")} data-testid="dn-invoice-no-input" />
          </div>
          {dirty && (
            <div className="col-span-2">
              <Button size="sm" variant="outline" onClick={saveRefs} disabled={saving} data-testid="dn-refs-save">
                {t("deliv.saveRefs")}
              </Button>
            </div>
          )}
        </div>

        <div className="max-h-[40vh] overflow-auto thin-scroll rounded-lg border border-border">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-muted/60 backdrop-blur">
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("deliv.colCode")}</TableHead>
                <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("common.product")}</TableHead>
                <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("deliv.colLot")}</TableHead>
                <TableHead className="h-9 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("deliv.colPackaged")}</TableHead>
                <TableHead className="h-9 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("deliv.colDaysLeft")}</TableHead>
                <TableHead className="h-9 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("deliv.colExpiry")}</TableHead>
                <TableHead className="h-9 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("deliv.colDelExpiry")}</TableHead>
                <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("deliv.colStorage")}</TableHead>
                <TableHead className="h-9 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("common.qty")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dn.lines?.map((ln, i) => {
                const meta = ptMeta[ln.product_type] || {};
                const code = ln.product_code || meta.code || "-";
                const temp = ln.storage_temp || meta.storage_temp || "-";
                const dl = daysLeft(ln.expiry_date);
                return (
                  <TableRow key={i} data-testid={`dn-detail-row-${i}`} className="even:bg-muted/20 hover:bg-muted/40">
                    <TableCell className="font-mono text-xs">{code}</TableCell>
                    <TableCell className="text-sm font-medium">{td(ln.product_type)} <span className="font-normal text-muted-foreground">({ln.unit})</span></TableCell>
                    <TableCell className="font-mono text-xs">{ln.lot_code || "-"}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums">{formatDate(ln.packaged_date, "-")}</TableCell>
                    <TableCell className="text-center text-xs">
                      {dl === null ? "-" : (dl < 0
                        ? <Badge variant="outline" className="border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]">{t("deliv.expired")}</Badge>
                        : <span className={dl <= 7 ? "font-medium text-[hsl(var(--warning))]" : ""}>{dl} {t("deliv.daysShort")}</span>)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums">{formatDate(ln.expiry_date, "-")}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums" data-testid={`dn-detail-delivery-expiry-${i}`}>{formatDate(ln.delivery_expiry_date, "-")}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{temp}</TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">{fmt(ln.quantity, 2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => onDownloadPacking(dn)} data-testid="dn-detail-packing">
            <FileText className="mr-1.5 h-4 w-4" /> {t("deliv.downloadPacking")}
          </Button>
          <Button onClick={() => onDownload(dn)} data-testid="dn-detail-download">
            <FileDown className="mr-1.5 h-4 w-4" /> {t("deliv.downloadCert")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
